import { usePageTitle } from '../../hooks/usePageTitle';
import GuideSidebar from '../../components/guides/GuideSidebar';
import GuideAccordion from '../../components/guides/GuideAccordion';
import ComparisonTable from '../../components/guides/ComparisonTable';
import RelatedListings from '../../components/guides/RelatedListings';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';

export default function TypesByPropulsion() {
  usePageTitle('Yacht Types by Propulsion | Motor, Sail & Hybrid');

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
        <div className="lg:col-span-1">
          <GuideSidebar />
        </div>

        <div className="lg:col-span-3">
          <div className="mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Yacht Types</p>
            <h1 className="text-4xl md:text-5xl font-medium tracking-tight mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Types by Propulsion: Motor, Sailing & Hybrid
            </h1>
            <p className="text-lg text-white/70 leading-relaxed">
              Understanding how yachts are powered is fundamental to choosing the right vessel for your Southeast Asian adventure.
            </p>
          </div>

          <div className="space-y-2">
            <GuideAccordion title="Motor Yachts (Power Yachts)" defaultOpen={true}>
              <p>
                Motor yachts, also called power yachts, rely entirely on engines for propulsion. They dominate the luxury charter market in Southeast Asia due to speed, comfort, and spacious interiors.
              </p>
              
              <h4 className="text-gold font-semibold mt-6 mb-3">Types of Motor Yachts:</h4>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-gold">•</span>
                  <div>
                    <strong className="text-white">Flybridge Motor Yachts:</strong> Most popular for charters. Features upper deck helm station with panoramic views, perfect for Southeast Asian cruising. Examples: Sunseeker, Azimut, Princess.
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="text-gold">•</span>
                  <div>
                    <strong className="text-white">Express Cruisers:</strong> Sleek, fast yachts with minimal superstructure. Ideal for day trips in the Andaman Sea (30-60 ft).
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="text-gold">•</span>
                  <div>
                    <strong className="text-white">Sportfish Yachts:</strong> Built for fishing but luxurious. Common in Philippines and Indonesia for marlin/tuna charters.
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="text-gold">•</span>
                  <div>
                    <strong className="text-white">Expedition Yachts:</strong> Long-range steel/aluminum vessels. Perfect for remote Mergui Archipelago or Raja Ampat with extended fuel capacity.
                  </div>
                </li>
              </ul>

              <ComparisonTable
                title="Motor Yacht Specifications"
                headers={['Type', 'Speed', 'Range', 'Fuel Cost', 'Southeast Asia Use']}
                rows={[
                  ['Flybridge 60-80ft', '18-25 knots', '300-600 nm', 'High', 'Island hopping, charters'],
                  ['Express Cruiser 40-60ft', '25-35 knots', '200-400 nm', 'Very High', 'Day trips, speed'],
                  ['Expedition 80-120ft', '12-18 knots', '3000+ nm', 'Moderate', 'Remote destinations'],
                  ['Sportfish 50-70ft', '20-30 knots', '400-800 nm', 'High', 'Fishing charters'],
                ]}
              />

              <div className="bg-white/5 p-6 mt-6 border-l-2 border-gold">
                <p className="text-white/90">
                  <strong>Southeast Asia Note:</strong> Motor yachts account for 70% of luxury charters in Thailand and Malaysia due to predictable schedules (no wind dependency) and onboard comfort with stabilizers.
                </p>
              </div>
            </GuideAccordion>

            <GuideAccordion title="Sailing Yachts">
              <p>
                Sailing yachts use wind power as primary propulsion, though modern vessels include auxiliary engines. They offer an authentic, eco-friendly yachting experience increasingly popular with environmentally conscious travelers.
              </p>

              <h4 className="text-gold font-semibold mt-6 mb-3">Types of Sailing Yachts:</h4>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-gold">•</span>
                  <div>
                    <strong className="text-white">Sloops:</strong> Single mast, most common. Excellent for cruising Thailand's coast (30-50 ft).
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="text-gold">•</span>
                  <div>
                    <strong className="text-white">Ketches:</strong> Two masts, easier sail handling. Popular for long-distance cruising to Indonesia.
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="text-gold">•</span>
                  <div>
                    <strong className="text-white">Schooners:</strong> Traditional two+ masts. Often seen in luxury charter fleets (80-120 ft).
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="text-gold">•</span>
                  <div>
                    <strong className="text-white">Phinisi:</strong> Traditional Indonesian wooden sailing yacht. Authentic Southeast Asian experience in Komodo/Flores.
                  </div>
                </li>
              </ul>

              <ComparisonTable
                title="Sailing Yacht Characteristics"
                headers={['Aspect', 'Sailing Yacht', 'Motor Yacht']}
                rows={[
                  ['Speed', '6-12 knots (wind dependent)', '18-30 knots (consistent)'],
                  ['Fuel Cost', '$50-150/day', '$500-3000/day'],
                  ['Noise Level', 'Silent under sail', 'Engine noise'],
                  ['Motion', 'Heeling (tilting) when sailing', 'Level with stabilizers'],
                  ['Experience', 'Active, participatory', 'Passive, relaxing'],
                  ['Eco-Friendly', 'Very (wind-powered)', 'Moderate (fuel-dependent)'],
                ]}
              />

              <div className="bg-white/5 p-6 mt-6 border-l-2 border-gold">
                <p className="text-white/90">
                  <strong>Sailing Season:</strong> November to April offers reliable northeast monsoon winds (10-20 knots) perfect for sailing in the Andaman Sea and South China Sea.
                </p>
              </div>
            </GuideAccordion>

            <GuideAccordion title="Hybrid & Electric Yachts">
              <p>
                The future of yachting: hybrid systems combine traditional engines with electric motors and solar panels, reducing emissions and noise while maintaining performance.
              </p>

              <h4 className="text-gold font-semibold mt-6 mb-3">Hybrid Technologies:</h4>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-gold">•</span>
                  <div>
                    <strong className="text-white">Diesel-Electric Hybrid:</strong> Generators charge batteries for silent cruising at low speeds. Ideal for anchorages in marine parks.
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="text-gold">•</span>
                  <div>
                    <strong className="text-white">Solar-Assisted:</strong> Solar panels supplement power for electronics, reducing generator use. Growing in Raja Ampat eco-charters.
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="text-gold">•</span>
                  <div>
                    <strong className="text-white">Fully Electric:</strong> Still rare in large yachts due to range limitations, but emerging for short-range luxury charters.
                  </div>
                </li>
              </ul>

              <div className="bg-white/5 p-6 mt-6 border-l-2 border-gold">
                <p className="text-white/90">
                  <strong>Southeast Asia Trend:</strong> Hybrid yachts are increasing in Indonesia and Thailand to meet marine park regulations and attract eco-conscious luxury travelers.
                </p>
              </div>
            </GuideAccordion>

            <GuideAccordion title="Choosing by Propulsion Type">
              <h4 className="text-gold font-semibold mb-3">Decision Guide:</h4>
              <div className="space-y-4">
                <div className="glass-light p-6">
                  <p className="text-gold font-semibold mb-2">Choose Motor if you want:</p>
                  <ul className="space-y-1 text-white/70">
                    <li>✓ Speed and predictable schedules</li>
                    <li>✓ Maximum comfort and space</li>
                    <li>✓ Short charters (3-7 days)</li>
                    <li>✓ Island hopping in Phuket, Langkawi, Bali</li>
                  </ul>
                </div>

                <div className="glass-light p-6">
                  <p className="text-gold font-semibold mb-2">Choose Sailing if you want:</p>
                  <ul className="space-y-1 text-white/70">
                    <li>✓ Authentic sailing experience</li>
                    <li>✓ Eco-friendly, silent cruising</li>
                    <li>✓ Lower operating costs</li>
                    <li>✓ Traditional phinisi in Indonesian waters</li>
                  </ul>
                </div>

                <div className="glass-light p-6">
                  <p className="text-gold font-semibold mb-2">Choose Hybrid if you want:</p>
                  <ul className="space-y-1 text-white/70">
                    <li>✓ Best of both worlds</li>
                    <li>✓ Silent anchoring in protected areas</li>
                    <li>✓ Reduced environmental impact</li>
                    <li>✓ Future-proof technology</li>
                  </ul>
                </div>
              </div>
            </GuideAccordion>
          </div>

          <RelatedListings yachtType="power" title="Browse Motor Yachts in Our Fleet" />

          <div className="mt-12 flex gap-4">
            <Link to="/guides/essentials">
              <Button variant="outline" className="border-gold text-gold hover:bg-gold hover:text-[#0A192F]">
                ← Back to Essentials
              </Button>
            </Link>
            <Link to="/guides/types-purpose">
              <Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]">
                Next: Types by Purpose →
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
